import type { MissatgeTempsReal } from '@/api/types';

export type EstatTempsReal = 'connectant' | 'connectat' | 'desconnectat';
export type Listener = (missatge: MissatgeTempsReal) => void;

export interface SocketLike {
  readyState: number;
  onopen: ((ev: Event) => void) | null;
  onclose: ((ev: CloseEvent) => void) | null;
  onmessage: ((ev: MessageEvent) => void) | null;
  onerror: ((ev: Event) => void) | null;
  send(data: string): void;
  close(): void;
}

export interface OpcionsRealtime {
  url: string;
  obteToken: () => Promise<string | null>;
  creaSocket?: (url: string) => SocketLike;
  intervalPingMs?: number;
  retardMaximMs?: number;
}

const OBERT = 1;

export class RealtimeClient {
  private socket: SocketLike | null = null;
  private readonly topics = new Map<string, Set<Listener>>();
  private readonly estatListeners = new Set<(e: EstatTempsReal) => void>();
  private readonly reconnexioListeners = new Set<() => void>();
  private haConnectat = false;
  private estatActual: EstatTempsReal = 'desconnectat';
  private intents = 0;
  private timerReconnexio: ReturnType<typeof setTimeout> | null = null;
  private timerPing: ReturnType<typeof setInterval> | null = null;
  private aturat = true;

  constructor(private readonly opcions: OpcionsRealtime) {}

  get estat(): EstatTempsReal {
    return this.estatActual;
  }

  onEstat(fn: (e: EstatTempsReal) => void): () => void {
    this.estatListeners.add(fn);
    return () => this.estatListeners.delete(fn);
  }

  /** S'invoca cada cop que el socket es torna a obrir després d'una caiguda (no la primera vegada). */
  onReconnecta(fn: () => void): () => void {
    this.reconnexioListeners.add(fn);
    return () => this.reconnexioListeners.delete(fn);
  }

  connecta(): void {
    this.aturat = false;
    void this.obre();
  }

  desconnecta(): void {
    this.aturat = true;
    this.netejaTimers();
    this.socket?.close();
    this.socket = null;
    this.canviaEstat('desconnectat');
  }

  subscriu(topic: string, listener: Listener): () => void {
    let set = this.topics.get(topic);
    if (!set) {
      set = new Set();
      this.topics.set(topic, set);
      this.envia({ type: 'subscribe', topic });
    }
    set.add(listener);
    return () => {
      const actual = this.topics.get(topic);
      if (!actual) return;
      actual.delete(listener);
      if (actual.size === 0) {
        this.topics.delete(topic);
        this.envia({ type: 'unsubscribe', topic });
      }
    };
  }

  private async obre(): Promise<void> {
    if (this.aturat) return;
    this.canviaEstat('connectant');
    const token = await this.opcions.obteToken().catch(() => null);
    if (this.aturat) return;
    const crea = this.opcions.creaSocket ?? ((u: string) => new WebSocket(u) as SocketLike);
    let socket: SocketLike;
    try {
      socket = crea(this.opcions.url);
    } catch {
      this.programaReconnexio();
      return;
    }
    this.socket = socket;
    socket.onopen = () => {
      this.intents = 0;
      if (token) socket.send(JSON.stringify({ type: 'auth', token }));
      this.topics.forEach((_, topic) => socket.send(JSON.stringify({ type: 'subscribe', topic })));
      this.iniciaPing();
      this.canviaEstat('connectat');
      if (this.haConnectat) this.reconnexioListeners.forEach((l) => l());
      this.haConnectat = true;
    };
    socket.onmessage = (ev) => this.rep(ev.data);
    socket.onerror = () => undefined;
    socket.onclose = () => {
      if (this.socket === socket) this.socket = null;
      this.netejaTimers();
      if (!this.aturat) this.programaReconnexio();
    };
  }

  private rep(data: unknown): void {
    if (typeof data !== 'string') return;
    let missatge: MissatgeTempsReal;
    try {
      missatge = JSON.parse(data) as MissatgeTempsReal;
    } catch {
      return;
    }
    if (!missatge.topic) return;
    this.topics.get(missatge.topic)?.forEach((l) => l(missatge));
  }

  private envia(missatge: MissatgeTempsReal): void {
    if (this.socket && this.socket.readyState === OBERT) {
      this.socket.send(JSON.stringify(missatge));
    }
  }

  private iniciaPing(): void {
    const interval = this.opcions.intervalPingMs ?? 25_000;
    this.timerPing = setInterval(() => this.envia({ type: 'ping' }), interval);
  }

  private programaReconnexio(): void {
    this.canviaEstat('desconnectat');
    const maxim = this.opcions.retardMaximMs ?? 30_000;
    const retard = Math.min(maxim, 1000 * 2 ** this.intents);
    this.intents += 1;
    this.timerReconnexio = setTimeout(() => void this.obre(), retard);
  }

  private netejaTimers(): void {
    if (this.timerPing) clearInterval(this.timerPing);
    if (this.timerReconnexio) clearTimeout(this.timerReconnexio);
    this.timerPing = null;
    this.timerReconnexio = null;
  }

  private canviaEstat(estat: EstatTempsReal): void {
    if (this.estatActual === estat) return;
    this.estatActual = estat;
    this.estatListeners.forEach((l) => l(estat));
  }
}
